import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import * as moment from "moment";
import PropTypes from "prop-types";
import { getDateFormat } from "@utils/datePickerUtils";
import { Image, AnchorWithEvent, WarningPopUp, Anchor } from "hesus-ui-kit";
import closeIcon from "@icons/ic-cancel.svg";
import gotoIcon from "@icons/ic-goto.svg";
import validateIcon from "@icons/ic-validate-green.svg";
import DocumentPreview from "../../Document/DocumentPreview/DocumentPreview";
import "./LeadView.scss";
import {
  LeadViewContainer,
  GridRow,
  MapContainer,
  GridSubItem,
  ParentDetail,
  DocumentName,
} from "./LeadView.styled";
import initMap from "@utils/mapSingleMarker";
import { avatarIcons, avatarIconsMr, avatarIconsMs } from "@utils/avatarInfo";
import LeadStatus from "./components/LeadStatus";
import DocumentType from "../../Document/DocumentList/components/DocumentType/DocumentType";
import tickIcon from "@icons/ic-tick-green.svg";
import closeOrgIcon from "@icons/ic-close-orange.svg";
import companyIcon from "@icons/ic-company.svg";
import priorityUrgentIcon from "@icons/ic-priority-urgent.svg";
import priorityNormalIcon from "@icons/ic-priority-normal.svg";
import priorityLowIcon from "@icons/ic-priority-low.svg";
import priorityNoIcon from "@icons/ic-no-priority.svg";

import LeadCompany from "./components/LeadCompany";
import AddEditEnquiry from "../../Enquiry/AddEditEnquiry";
import Operation from "./components/Operation";
import AddEditContact from "../../Contact/AddEditContact/AddEditContact";
import NotFound from "@components/Errors/NotFound";

/**
 * Lead View Component
 * Path - /lead/view/{id}
 */
const LeadView = (props) => {
  const { t } = props;

  const [avatarImage, setAvatarImage] = useState(avatarIcons[1]);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const openPreview = (document) => setSelectedDocument(document);
  const closePreview = () => setSelectedDocument(null);
  const avatarNumber = Math.floor(Math.random() * 10);

  const params = useParams();
  const navigate = useNavigate();
  const history = useNavigate();

  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConfirmVisible, setConfirmVisibility] = useState(false);
  const [isConfirm, setActionConfirmStatus] = useState(false);
  const [isRefreshList, setIsRefreshList] = useState(false);

  const [showCompanyPopUp, setShowCompanyPopUp] = useState(false);
  const [isAddEnquiryPopupVisible, setAddEnquiryPopupVisible] = useState(false);
  const [isAddContactPopupVisible, setAddContactPopupVisible] = useState(false);
  const [isViewContactPopupVisible, setViewContactPopupVisible] =
    useState(false);
  const [enquiryData, setEnquiryData] = useState([]);
  const [isContactValidated, setIsContactValidated] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  const viewCompanyPopUp = () => {
    setShowCompanyPopUp(true);
  };

  const onCompanyClosePopUp = () => {
    setShowCompanyPopUp(false);
  };

  const viewContactPopUp = () => {
    setViewContactPopupVisible(true);
  };

  const addContactPopUp = () => {
    setAddContactPopupVisible(true);
  };

  const closeContactCreationPopup = () => {
    setAddContactPopupVisible(false);
    setViewContactPopupVisible(false);
  };

  const createSiteForLead = (id) => {
    window.open(`/site/create?leadId=${id}`, "_self");
  };

  const updateLeadStatus = (status) => {
    const tempData = { ...leadData, status };
    axios
      .put(`/leads/${leadData.id}`, tempData)
      .then((response) => {
        setActionConfirmStatus(false);
        setLeadData(response?.data[0]);
        setIsRefreshList(true);
      })
      .catch((error) => handleApiError(error));
  };

  const updateLeadStep = (step) => {
    const tempData = { ...leadData, step };
    axios
      .put(`/leads/${leadData.id}`, tempData)
      .then((response) => {
        setLeadData(response?.data[0]);
        setIsRefreshList(true);
      })
      .catch((error) => handleApiError(error));
  };

  useEffect(() => {
    if (isContactValidated) {
      updateLeadStep(2);
    }
  }, [isContactValidated]);

  useEffect(() => {
    if (isConfirm) {
      updateLeadStatus(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirm]);

  useEffect(() => {
    if (
      !!leadData?.siteAddress?.position?.latitude &&
      !!leadData?.siteAddress?.position?.longitude
    )
      initMap(
        leadData.siteAddress.position.latitude,
        leadData.siteAddress.position.longitude,
        false
      );
  }, [leadData]);

  const onPopUpCancel = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(false);
  };

  const onPopUpConfirm = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(true);
  };

  useEffect(() => {
    fetchLeadData();
  }, [params.id]);

  const setAvatarImageBasedOnCourtesey = (courtesy) => {
    if (courtesy === "MISTER") {
      setAvatarImage(avatarIconsMr[avatarNumber] ?? avatarIcons[1]);
    } else if (courtesy === "MISS") {
      setAvatarImage(avatarIconsMs[avatarNumber] ?? avatarIcons[4]);
    } else {
      setAvatarImage(avatarIcons[avatarNumber] ?? avatarIcons[1]);
    }
  };

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/leads/${params.id}`);
      setLeadData(response.data[0]);
      setAvatarImageBasedOnCourtesey(response.data[0].contact.courtesy);
    } catch (err) {
      if (err?.response?.status === 404) {
        setIsNotFound(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getPriorityIcon = (priority) => {
    let icon = priorityNoIcon;
    if (priority === 3) {
      icon = priorityLowIcon;
    }
    if (priority === 2) {
      icon = priorityNormalIcon;
    }
    if (priority === 1) {
      icon = priorityUrgentIcon;
    }
    return icon;
  };

  const getPriorityText = (priority) => {
    let icon = "ENQUIRY_PRIORITY_NOT_DEFINED";
    if (priority === 3) {
      icon = "ENQUIRY_PRIORITY_LOW";
    }
    if (priority === 2) {
      icon = "ENQUIRY_PRIORITY_NORMAL";
    }
    if (priority === 1) {
      icon = "ENQUIRY_PRIORITY_HIGH";
    }
    return icon;
  };
  useEffect(() => {
    if (isRefreshList) {
      setIsRefreshList(false);
      fetchLeadData();
    }
  }, [isRefreshList]);

  const getProgressSteps = () => {
    const step = leadData?.step || 0;

    return [
      {
        key: "company_selection",
        label: t("LEAD_STEP_COMPANY_SELECTION"),
        completed: step >= 1,
      },
      {
        key: "contact_validation",
        label: t("LEAD_STEP_CONTACT_VALIDATION"),
        completed: step >= 2,
      },
      {
        key: "site_creation",
        label: t("LEAD_STEP_SITE_CREATION"),
        completed: step >= 3,
      },
      {
        key: "enquiries_creation",
        label: t("LEAD_STEP_ENQUIRIES_CREATION"),
        completed: step >= 4,
      },
    ];
  };

  if (loading) {
    return <div className="lead-view-loading">Loading...</div>;
  }

  // if (error || !leadData) {
  //   return <NotFound />;
  // }

  const requestCreateForLead = (enquiryInfo) => {
    if (enquiryInfo?.id) setEnquiryData(enquiryInfo);
    else setEnquiryData(leadData?.leadEnquiries?.[0]);
    setAddEnquiryPopupVisible(true);
  };
  const closeEnquiryCreationPopup = () => {
    setEnquiryData([]);
    setAddEnquiryPopupVisible(false);
  };
  const updateRefeshStatus = () => {
    setIsRefreshList(true);
  };
  const progressSteps = getProgressSteps();

  if ((isNotFound || error) || !leadData) {
    return <NotFound />;
  }

  return (
    <LeadViewContainer>
      {/* Header Section */}
      <div className="lead-header">
        <div className="lead-header-left">
          <div className="lead-title">
            <span className="lead-id">
              {"#" + leadData.id} - {leadData.contact?.firstName}{" "}
              {leadData.contact?.lastName} / {leadData.address?.city}
            </span>
          </div>
        </div>
        <div className="lead-header-right">
          <LeadStatus data={leadData.status} />
          {(leadData.status === 0 || leadData.status === 2) && (
            <Image
              src={closeIcon}
              alt={t("LEADS_REJECT_ACTION_TOOLTIP")}
              title={t("LEADS_REJECT_ACTION_TOOLTIP")}
              className="lead-view-header__image-icon"
              onClick={() => setConfirmVisibility(true)}
            />
          )}
          {leadData.status === 2 && leadData.step === 4 && (
            <Image
              src={validateIcon}
              alt={t("LEADS_VALIDATE_ACTION_TOOLTIP")}
              title={t("LEADS_VALIDATE_ACTION_TOOLTIP")}
              className="lead-view-header__image-icon"
              onClick={() => updateLeadStatus(4)}
            />
          )}
        </div>
      </div>

      {/* Progress Tracker */}
      {leadData.step === 4 ? (
        <div className="progress-tracker completed-all">
          <div className="step-icon">
            <Image src={tickIcon} className="completion-icon" alt="Completed" />
          </div>
          <div className="completion-message">
            <span>{t("LEAD_ALL_STEPS_DONE")}</span>
          </div>
        </div>
      ) : (
        <div className="progress-tracker">
          <div className="buffer"></div>
          {progressSteps.map((step, index) => (
            <div
              key={step.key}
              className={`progress-step ${
                step.completed ? "completed" : "pending"
              }`}
            >
              <div className="step-icon">
                <Image
                  src={step.completed ? tickIcon : closeOrgIcon}
                  className="section-icon"
                  alt={step.completed ? "Completed" : "Pending"}
                />
              </div>
              <div className="step-label">{step.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="lead-content">
        {/* Company Selection and Contact Validation Row */}
        <div className="content-row">
          <div className="content-section company-selection">
            <div className="section-header">
              <Image
                src={leadData.step >= 1 ? tickIcon : closeOrgIcon}
                className="section-icon"
              />
              <h3>{t("LEAD_SECTION_TITLE_COMPANY")}</h3>
            </div>
            <GridRow
              column={leadData.step === 0 ? "10% 80% auto" : "10% auto"}
              gap="0.25rem"
            >
              {leadData.step === 0 && (
                <>
                  <div></div>
                  <div className="company-info">
                    <div className="company-id">
                      {leadData.tempCompany?.name}
                    </div>
                    <div className="company-details">
                      <div>
                        <span className="details-label">
                          {t("LEAD_COMPANY_REGISTRATION")}
                        </span>
                        : {leadData.tempCompany?.registrationNumber}
                      </div>
                      <div>
                        <span className="details-label">
                          {t("LEAD_COMPANY_ADDRESS")}
                        </span>
                        : {leadData.tempCompany?.address?.address}
                        {leadData.tempCompany?.address?.zipcode
                          ? ", " + leadData.tempCompany?.address?.zipcode
                          : ""}
                        {leadData.tempCompany?.address?.city
                          ? ", " + leadData.tempCompany?.address?.city
                          : ""}
                      </div>
                    </div>
                  </div>
                  <div className="lead-header-right">
                    <Image
                      src={gotoIcon}
                      alt={t("LEAD_COMPANY_TOOLTIP_SELECT_COMPANY")}
                      title={t("LEAD_COMPANY_TOOLTIP_SELECT_COMPANY")}
                      className="goto-icon"
                      onClick={viewCompanyPopUp}
                    />
                  </div>
                </>
              )}
              {leadData.step > 0 && (
                <>
                  <Image
                    src={companyIcon}
                    alt={t("LEADS_REJECT_ACTION_TOOLTIP ")}
                    title={t("LEADS_REJECT_ACTION_TOOLTIP ")}
                    className="company-image-icon"
                    styles={{ height: "30px", width: "30px", margin: "16px" }}
                  />
                  <div className="company-info">
                    <Link
                      className="site-view-header__parent-link"
                      to={`/company/view/${leadData.company?.id}/general-info`}
                    >
                      <span className="site-id">#{leadData.company?.id}</span> -{" "}
                      <span className="company-name">
                        {leadData.company?.name}
                      </span>
                    </Link>

                    <div className="company-details">
                      <div>
                        <span className="details-label">
                          {t("LEAD_COMPANY_REGISTRATION")}
                        </span>
                        : {leadData.company?.registrationNumber}
                      </div>
                      <div>
                        <span className="details-label">
                          {t("LEAD_COMPANY_ADDRESS")}
                        </span>
                        : {leadData.company?.billingAddress?.address}
                        {leadData.company?.billingAddress?.zipcode
                          ? ", " + leadData.company?.billingAddress?.zipcode
                          : ""}
                        {leadData.company?.billingAddress?.city
                          ? ", " + leadData.company?.billingAddress?.city
                          : ""}
                      </div>
                      <div className="company-initial-value">
                        <span className="details-label">
                          {t("LEAD_COMPANY_INITIAL_VALUE")}
                        </span>
                        : {leadData.tempCompany?.name}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </GridRow>
          </div>

          <div className="content-section contact-validation">
            <div className="section-header">
              <Image
                src={leadData.step > 1 ? tickIcon : closeOrgIcon}
                className="section-icon"
              />
              <h3>{t("LEAD_SECTION_TITLE_CONTACT")}</h3>
            </div>
            <GridRow
              column={leadData.step > 1 ? "10% auto" : "10% 80% auto"}
              gap="0.25rem"
            >
              {avatarImage && (
                <Image
                  src={avatarImage}
                  alt="Avatar"
                  styles={{ height: "30px", width: "30px", margin: "16px" }}
                />
              )}

              <div className="contact-info">
                {leadData.step < 2 && (
                  <div className="contact-id">
                    {leadData.contact?.firstName} {leadData.contact?.lastName}
                  </div>
                )}
                {leadData.step > 1 && (
                  <div className="contact-id">
                    <AnchorWithEvent
                      className="site-view-header__parent-link"
                      clickAction={() => viewContactPopUp()}
                    >
                      #{leadData.contact?.id} - {leadData.contact?.name}
                    </AnchorWithEvent>
                  </div>
                )}

                <div className="contact-email">{leadData.contact?.email}</div>
                <div className="contact-phone">{leadData.contact?.phone}</div>
              </div>
              {leadData.step == 1 && (
                <div className="lead-header-right">
                  <Image
                    src={gotoIcon}
                    className="goto-icon"
                    alt={t("LEAD_CONTACT_TOOLTIP_VALIDATE_CONTACT")}
                    title={t("LEAD_CONTACT_TOOLTIP_VALIDATE_CONTACT")}
                    onClick={() => addContactPopUp()}
                  />
                </div>
              )}
            </GridRow>
          </div>
        </div>

        {/* Site Information Row */}
        <div className="content-row">
          <div className="content-section site-information">
            <div className="section-header">
              <Image
                src={leadData.step > 2 ? tickIcon : closeOrgIcon}
                className="section-icon"
              />
              <h3>{t("LEAD_SECTION_TITLE_SITE")}</h3>
              {leadData.step === 2 && (
                <button
                  className="create-btn"
                  onClick={() => createSiteForLead(leadData?.id)}
                >
                  {t("LEAD_CREATE_SITE")}
                </button>
              )}
            </div>

            <div className="site-details">
              <GridRow column="1fr 400px">
                <div className="site-info-left">
                  {(leadData.step === 3 || leadData.step === 4) && (
                    <Link
                      className="site-view-header__parent-link"
                      to={`/site/view/${leadData.site?.id}/general-info`}
                    >
                      <span className="site-id">#{leadData.site?.id}</span> -{" "}
                      <span className="company-name">
                        {leadData.site?.name}
                      </span>
                    </Link>
                  )}
                  <div className="info-row">
                    <label>{t("LEAD_SITE_ESTIMATED_DATES")}:</label>
                    <span>
                      {getDateFormat(
                        moment(leadData.startDate, "DD/MM/YYYY"),
                        "YYYY-MM-DD"
                      )}
                      {" - "}
                      {leadData.endDate
                        ? getDateFormat(
                            moment(leadData.endDate, "DD/MM/YYYY"),
                            "YYYY-MM-DD"
                          )
                        : "?"}
                    </span>
                  </div>
                  <div className="info-row">
                    <label>{t("LEAD_SITE_TYPE")}:</label>
                    <span>
                      {leadData.isTender
                        ? t("SITE_TYPE_TENDER")
                        : t("SITE_TYPE_LIVE")}
                    </span>
                  </div>
                  <div className="info-row">
                    <label>{t("LEAD_SITE_ADDRESS")}:</label>
                    <span>
                      {leadData.step < 3 && (
                        <>
                          {leadData.address?.street}
                          {leadData.address?.street && leadData.address?.city
                            ? ", " + leadData.address?.city
                            : leadData.address?.city}
                          {leadData.address?.city
                            ? ", " + leadData.address?.zipcode
                            : leadData.address?.zipcode}
                        </>
                      )}
                      {leadData.step >= 3 && (
                        <>
                          {leadData.siteAddress?.street}
                          {leadData.siteAddress?.street &&
                          leadData.siteAddress?.city
                            ? ", " + leadData.siteAddress?.city
                            : leadData.siteAddress?.city}
                          {leadData.siteAddress?.city
                            ? ", " + leadData.siteAddress?.zipcode
                            : leadData.siteAddress?.zipcode}
                        </>
                      )}
                    </span>
                  </div>

                  <div className="documents-section">
                    <label>{t("LEAD_SITE_DOCUMENTS")}:</label>
                    <div className="document-list">
                      {leadData.documents &&
                        leadData.documents.length > 0 &&
                        leadData.documents.map((doc, index) => (
                          <div key={index} className="document-item">
                            {/* <span className="document-type">Other</span>
                            <a href={doc.downloadLink} className="document-link">{doc.name}</a> */}
                            <DocumentType type={doc.type} />

                            <DocumentName
                              onClick={() => openPreview(doc)}
                              id={`document-tooltip-id${doc.id}`}
                              className="document-list__document-link"
                            >
                              {doc?.fileName}
                            </DocumentName>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="lead-comment">
                    <label>{t("LEAD_COMMENT")}:</label>
                    <div className="comment-text">
                      <div
                        dangerouslySetInnerHTML={{ __html: leadData.comment }}
                      />
                    </div>
                  </div>
                </div>

                <div className="site-info-right">
                  {!!leadData.siteAddress?.position?.latitude &&
                    !!leadData.siteAddress?.position?.longitude && (
                      <MapContainer id="map" height="300px;" />
                    )}
                </div>
              </GridRow>
            </div>
          </div>
        </div>

        {/* Lead Enquiries Row */}
        <div className="content-row">
          <div className="content-section lead-enquiries">
            <div className="section-header">
              <Image
                src={leadData.step > 3 ? tickIcon : closeOrgIcon}
                className="section-icon"
              />
              <h3>{t("LEAD_SECTION_TITLE_ENQUIRIES")}</h3>
            </div>
            <div className="enquiries-list">
              {leadData.leadEnquiries?.map((enquiry, index) => (
                <div key={index} className="enquiry-item">
                  <GridRow key={index} column="5% 85% auto" gap="0.5rem">
                    <GridSubItem>
                      <Image
                        className="dashboard-widget-view-site__image_view_icon"
                        src={getPriorityIcon(parseInt(enquiry?.priority, 10))}
                        alt={t(
                          getPriorityText(parseInt(enquiry?.priority, 10))
                        )}
                        title={t(
                          getPriorityText(parseInt(enquiry?.priority, 10))
                        )}
                      />
                    </GridSubItem>

                    <GridSubItem
                      fontWeight="700"
                      fontStyle="normal"
                      fontSize="16px"
                    >
                      {enquiry.enquiryId && (
                        <Anchor
                          url={`/site/view/${leadData.site.id}/enquiry-view-${enquiry.enquiryId}`}
                          className="dashboard-widget-view-site__link"
                          target="_blank"
                        >
                          {"#" + enquiry.enquiryId} -{" "}
                          {t(enquiry.subCategory?.name)}
                          {enquiry.product?.name
                            ? " - " + enquiry.product?.name
                            : ""}
                        </Anchor>
                      )}
                      {!enquiry.enquiryId && (
                        <span>
                          {t(enquiry.subCategory?.name)}{" "}
                          {enquiry.product?.name
                            ? " - " + enquiry.product?.name
                            : ""}
                        </span>
                      )}
                      <div className="enquiry-details">
                        <Operation data={enquiry} />
                        <span className="enquiry-quantity">
                          {enquiry?.quantity + " " + t(enquiry?.unit) + " - "}
                          {enquiry.dateType === 1 &&
                            getDateFormat(enquiry.startDate, "YYYY-MM-DD")}
                          {enquiry.dateType !== 1 &&
                            t("GENERAL_FROM") +
                              " " +
                              getDateFormat(enquiry.startDate, "YYYY-MM-DD") +
                              " " +
                              t("GENERAL_TO") +
                              " " +
                              getDateFormat(enquiry.endDate, "YYYY-MM-DD")}
                        </span>
                      </div>
                    </GridSubItem>
                    <GridRow column="auto auto" gap="0.25rem">
                      {(leadData.step === 3 || leadData.step === 4) &&
                        leadData.status === 2 &&
                        enquiry.enquiryId === null && (
                          <Image
                            src={gotoIcon}
                            alt={t("LEAD_ENQUIRY_TOOLTIP_CREATE_ENQUIRY")}
                            title={t("LEAD_ENQUIRY_TOOLTIP_CREATE_ENQUIRY")}
                            className="goto-icon"
                            onClick={() => requestCreateForLead(enquiry)}
                          />
                        )}
                    </GridRow>
                  </GridRow>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {isConfirmVisible && (
        <WarningPopUp
          title={t("LEAD_REJECTION_POPUP_TITLE")}
          message={t("LEAD_REJECTION_POPUP_MESSAGE")}
          onPopUpCancel={onPopUpCancel}
          onPopUpConfirm={onPopUpConfirm}
          ValidateContent={t("YES")}
          CancelContent={t("CANCEL")}
        />
      )}
      {showCompanyPopUp && (
        <LeadCompany
          onClose={onCompanyClosePopUp}
          companyInfo={leadData.tempCompany}
          leadId={leadData.id}
          leadCountry={leadData.country.id}
          setIsRefreshList={setIsRefreshList}
        />
      )}
      {isAddEnquiryPopupVisible && (
        <AddEditEnquiry
          title={t("ENQUIRY")}
          onClose={closeEnquiryCreationPopup}
          updateRefeshStatus={updateRefeshStatus}
          siteId={leadData?.site?.id}
          platformCountryId={leadData?.site?.platformCountryId}
          enquiryData={enquiryData}
          isEditMode={false}
          leadId={leadData.id}
        />
      )}
      {isViewContactPopupVisible && (
        <AddEditContact
          title={t("Contact")}
          onClose={closeContactCreationPopup}
          updateRefeshStatus={updateRefeshStatus}
          isEditMode={false}
          isViewMode
          isViewContact
          companyId={parseInt(leadData?.company?.id, 10)}
          contactData={leadData?.contact}
          countryId={leadData?.country?.id}
        />
      )}
      {isAddContactPopupVisible && (
        <AddEditContact
          title={t("Contact")}
          onClose={closeContactCreationPopup}
          updateRefeshStatus={updateRefeshStatus}
          isEditMode
          page="lead"
          setIsContactValidated={setIsContactValidated}
          companyId={parseInt(leadData?.company?.id, 10)}
          contactData={leadData?.contact}
          id={leadData?.contact?.id}
          leadId={leadData?.id}
          countryId={leadData?.country?.id}
        />
      )}
      {selectedDocument && (
        <DocumentPreview document={selectedDocument} onClose={closePreview} />
      )}
    </LeadViewContainer>
  );
};

LeadView.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(LeadView);
